import { isPast } from 'date-fns';
import { useRouter } from 'next/router';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { Icon } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import { Col, Row } from 'components/layout';

import { useCohost } from 'hooks/useCohost';

import { TouchpointState } from 'lib/models/opportunity';
import { StudentSocietyResponse } from 'lib/models/student-society';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';

import styles from './PendingInviteCard.module.scss';

export const PendingInviteCard: React.FC = memo(() => {
  const router = useRouter();
  const { id: touchpointId } = router.query;

  const [t] = useTranslation('event-dashboard');

  const {
    receivedCohostInvites: receivedCohostInvitesResponse,
    onAcceptInvite,
    onDeclineInvite,
  } = useCohost();

  const pendingInvites = useMemo(() => {
    if (receivedCohostInvitesResponse) {
      const receivedCohostInvites = parseArrayResponse(
        receivedCohostInvitesResponse
      );
      const invites = touchpointId
        ? receivedCohostInvites.filter(
            ({ touchpoint_id }) => touchpoint_id === touchpointId
          )
        : receivedCohostInvites;
      return invites.map((item) => {
        const { id, status, touchpoint: touchpointResponse } = item;
        const touchpoint = parseResponse(touchpointResponse);
        const {
          id: touchpointId,
          title,
          slug: event_slug,
          creatable_for,
          start_date,
        } = touchpoint;
        const { icon_url, name, slug } = parseResponse(
          creatable_for as StudentSocietyResponse
        );
        return {
          id,
          icon_url,
          name,
          slug,
          title,
          event_slug,
          status,
          touchpointId,
          start_date,
        };
      });
    }
  }, [receivedCohostInvitesResponse]);

  return (
    <>
      {pendingInvites &&
        pendingInvites
          .filter(
            (invite) =>
              invite.status === TouchpointState.PENDING &&
              !isPast(new Date(invite?.start_date))
          )
          .map(({ id, name, title }) => (
            <div key={id} className={styles.PendingInviteCard}>
              <Row align="center" justify="space-between">
                <Col>
                  <Row className={styles.info}>
                    <Col>
                      <Icon iconName="icon_info" size="large" />
                    </Col>
                    <Col className={styles.infoText}>
                      <p className={styles.title}>{t('invite-card.title')}</p>
                      <p className={styles.desc}>
                        {t('invite-card.title', { name, title })}
                      </p>
                    </Col>
                  </Row>
                </Col>
                <Col className={styles.buttons}>
                  <ButtonV3
                    onClick={() => {
                      onAcceptInvite(id);
                    }}
                    color="secondary"
                    borderless
                  >
                    {t('common.accept')}
                  </ButtonV3>
                  <ButtonV3
                    onClick={async () => {
                      await onDeclineInvite(id);
                      router.push(PAGE_ROUTES.CAMPAIGNS);
                    }}
                    color="secondary"
                    borderless
                  >
                    {t('common.decline')}
                  </ButtonV3>
                </Col>
              </Row>
            </div>
          ))}
    </>
  );
});

PendingInviteCard.displayName = 'PendingInviteCard';
